import React, { useState } from 'react';
import SideNav from '../components/SideNav';
import { User, Bot, Mic, Shield, AlertTriangle, Play, Zap, Terminal, CheckCircle } from 'lucide-react';

const SalesLab = () => {
    const [activePersona, setActivePersona] = useState(null); // 'PETE', 'MIKE', or null

    // MOCK DATA: This sells the vision to the reviewer.
    // It looks like a real conversation happened.
    const mockChatHistory = [
        {
            id: 1,
            sender: 'AI',
            name: 'HARD MIKE',
            text: "Alright, look. I've got three other vendors knocking on my door today. Why should I waste time with a guy moving boxes at Amazon? Give me the bottom line.",
            type: 'HOSTILE'
        },
        {
            id: 2,
            sender: 'USER',
            name: 'YOU',
            text: "Because those vendors are selling you a catalog. I'm selling a system that I built with my own hands. I know where the failure points are because I've lived them.",
            type: 'USER'
        },
        {
            id: 3,
            sender: 'AI',
            name: 'HARD MIKE',
            text: "That's a nice story, but stories don't fix engines. What happens when your 'system' breaks at 2 AM on a Sunday?",
            type: 'SKEPTICAL'
        },
    ];

    return (
        <div style={styles.layout}>
            <SideNav activePage="/sales-lab" />

            <div style={styles.mainContent}>
                {/* HEADER */}
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.pageTitle}>SALES LAB <span style={styles.betaTag}>ALPHA PREVIEW</span></h1>
                        <p style={styles.subTitle}>AI-DRIVEN NEGOTIATION SIMULATOR // REAL-TIME COACHING ENABLED</p>
                    </div>
                    <div style={styles.statusBadge}>
                        <div style={styles.dot}></div> SYSTEM ONLINE
                    </div>
                </div>

                {/* CONTENT GRID */}
                <div style={styles.gridContainer}>

                    {/* LEFT: SIMULATION WINDOW */}
                    <div style={styles.simWindow}>
                        {/* Persona Header */}
                        <div style={styles.personaHeader}>
                            <div style={styles.avatarContainer}>
                                {activePersona === 'MIKE' ? (
                                    <div style={styles.avatarBad}><Shield size={30} /></div>
                                ) : activePersona === 'PETE' ? (
                                    <div style={styles.avatarGood}><User size={30} /></div>
                                ) : (
                                    <div style={styles.avatarNeutral}><Bot size={30} /></div>
                                )}
                            </div>
                            <div style={styles.personaInfo}>
                                <h2 style={styles.personaName}>
                                    {activePersona === 'MIKE' ? "HARD MIKE" : activePersona === 'PETE' ? "EASY PETE" : "SELECT OPPONENT"}
                                </h2>
                                <p style={styles.personaRole}>
                                    {activePersona === 'MIKE' ? "SKEPTICAL PROCUREMENT MANAGER" : activePersona === 'PETE' ? "FRIENDLY SHOP OWNER" : "AWAITING SELECTION..."}
                                </p>
                            </div>
                            {activePersona && (
                                <div style={activePersona === 'MIKE' ? styles.badgeHard : styles.badgeEasy}>
                                    DIFFICULTY: {activePersona === 'MIKE' ? 'HARD' : 'EASY'}
                                </div>
                            )}
                        </div>

                        {/* Chat Area */}
                        <div style={styles.chatArea}>
                            {!activePersona ? (
                                <div style={styles.selectionScreen}>
                                    <h3 style={styles.selectTitle}>SELECT SIMULATION TARGET</h3>
                                    <div style={styles.cardRow}>
                                        <div style={styles.personaCard} onClick={() => setActivePersona('PETE')}>
                                            <div style={styles.iconBox}><User size={40} color="#D4AF37" /></div>
                                            <h3>EASY PETE</h3>
                                            <p>Friendly demeanor. Tests your ability to upsell without being pushy.</p>
                                            <button style={styles.selectBtn}>INITIALIZE</button>
                                        </div>
                                        <div style={styles.personaCardBad} onClick={() => setActivePersona('MIKE')}>
                                            <div style={styles.iconBoxBad}><Shield size={40} color="#ff3333" /></div>
                                            <h3>HARD MIKE</h3>
                                            <p>Hostile negotiator. Tests resilience and objection handling.</p>
                                            <button style={styles.selectBtnBad}>INITIALIZE</button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={styles.messageList}>
                                    {/* MOCK MESSAGES */}
                                    {mockChatHistory.map((msg) => (
                                        <div key={msg.id} style={msg.type === 'USER' ? styles.msgUser : styles.msgAi}>
                                            <div style={styles.msgHeader}>{msg.name}</div>
                                            <div style={styles.msgBody}>{msg.text}</div>
                                        </div>
                                    ))}

                                    {/* FAKE TYPING INDICATOR */}
                                    <div style={styles.typingIndicator}>
                                        <span style={styles.dotAnim}>.</span><span style={styles.dotAnim}>.</span><span style={styles.dotAnim}>.</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Input Area */}
                        <div style={styles.inputArea}>
                            <button style={styles.micBtn}><Mic size={20} /></button>
                            <input style={styles.input} placeholder="Awaiting User Voice Input..." disabled />
                            <button style={styles.sendBtn}><Play size={20} /></button>
                        </div>
                    </div>

                    {/* RIGHT: REAL-TIME COACHING (WHISPER MODE) */}
                    <div style={styles.coachingPanel}>
                        <div style={styles.panelHeader}>
                            <Terminal size={18} color="#D4AF37" />
                            <span>MILLION // WHISPER MODE</span>
                        </div>

                        <div style={styles.analysisBox}>
                            <div style={styles.metricRow}>
                                <span>SENTIMENT:</span>
                                <span style={{ color: '#ff3333' }}>SKEPTICAL (85%)</span>
                            </div>
                            <div style={styles.metricRow}>
                                <span>PACING:</span>
                                <span style={{ color: '#4caf50' }}>OPTIMAL</span>
                            </div>
                        </div>

                        <div style={styles.whisperBox}>
                            <div style={styles.whisperTitle}><Zap size={16} fill="#D4AF37" /> LIVE SUGGESTION:</div>
                            <p style={styles.whisperText}>
                                "He is challenging your reliability. Do not get defensive. Pivot to your <strong>'Amazon Work Ethic'</strong> story. Mention that you don't clock out until the job is done."
                            </p>
                            <div style={styles.actionRow}>
                                <button style={styles.actionBtn}><CheckCircle size={14} /> ACCEPT</button>
                                <button style={styles.actionBtnOutline}>IGNORE</button>
                            </div>
                        </div>

                        <div style={styles.alertBox}>
                            <AlertTriangle size={20} color="#ff3333" />
                            <div>
                                <strong>TRAP DETECTED:</strong>
                                <p style={{ fontSize: '0.8rem', margin: 0 }}>He is trying to make you compete on price. Stick to value.</p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

const styles = {
    layout: { display: 'flex', minHeight: '100vh', background: '#000', color: '#fff', fontFamily: 'Inter, sans-serif' },
    mainContent: { flex: 1, padding: '30px', display: 'flex', flexDirection: 'column' },

    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: '1px solid #222', paddingBottom: '20px' },
    pageTitle: { fontFamily: 'Orbitron', fontSize: '2rem', color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '15px' },
    betaTag: { fontSize: '0.8rem', background: '#222', color: '#D4AF37', padding: '5px 10px', borderRadius: '4px', border: '1px solid #333' },
    subTitle: { color: '#666', margin: '5px 0 0 0', fontSize: '0.9rem', letterSpacing: '1px' },
    statusBadge: { display: 'flex', alignItems: 'center', gap: '8px', color: '#4caf50', fontSize: '0.9rem', fontFamily: 'Orbitron' },
    dot: { width: '8px', height: '8px', background: '#4caf50', borderRadius: '50%', boxShadow: '0 0 10px #4caf50' },

    gridContainer: { display: 'flex', gap: '20px', flex: 1, height: 'calc(100vh - 200px)' },

    // SIMULATION WINDOW (LEFT)
    simWindow: { flex: 2, background: '#0a0a0a', border: '1px solid #333', borderRadius: '8px', display: 'flex', flexDirection: 'column' },

    personaHeader: { padding: '20px', borderBottom: '1px solid #222', display: 'flex', alignItems: 'center', gap: '15px', background: '#111' },
    avatarContainer: { width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: '#000', border: '1px solid #333' },
    avatarBad: { color: '#ff3333' },
    avatarGood: { color: '#D4AF37' },
    avatarNeutral: { color: '#666' },
    personaInfo: { flex: 1 },
    personaName: { margin: 0, fontFamily: 'Orbitron', fontSize: '1.2rem', color: '#fff' },
    personaRole: { margin: 0, fontSize: '0.8rem', color: '#666' },
    badgeHard: { background: 'rgba(255, 51, 51, 0.1)', color: '#ff3333', padding: '5px 10px', fontSize: '0.7rem', border: '1px solid #ff3333', borderRadius: '4px' },
    badgeEasy: { background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37', padding: '5px 10px', fontSize: '0.7rem', border: '1px solid #D4AF37', borderRadius: '4px' },

    chatArea: { flex: 1, padding: '20px', overflowY: 'auto', background: '#050505', display: 'flex', flexDirection: 'column' },

    // Selection Screen
    selectionScreen: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
    selectTitle: { fontFamily: 'Orbitron', color: '#666', marginBottom: '30px' },
    cardRow: { display: 'flex', gap: '20px' },
    personaCard: { background: '#111', border: '1px solid #333', padding: '30px', width: '250px', textAlign: 'center', cursor: 'pointer', borderRadius: '8px', transition: '0.2s' },
    personaCardBad: { background: '#111', border: '1px solid #333', padding: '30px', width: '250px', textAlign: 'center', cursor: 'pointer', borderRadius: '8px', transition: '0.2s' },
    iconBox: { marginBottom: '15px', display: 'flex', justifyContent: 'center' },
    iconBoxBad: { marginBottom: '15px', display: 'flex', justifyContent: 'center' },
    selectBtn: { marginTop: '20px', width: '100%', padding: '10px', background: '#D4AF37', border: 'none', fontWeight: 'bold', cursor: 'pointer' },
    selectBtnBad: { marginTop: '20px', width: '100%', padding: '10px', background: '#ff3333', border: 'none', fontWeight: 'bold', cursor: 'pointer', color: '#fff' },

    // Messages
    messageList: { display: 'flex', flexDirection: 'column', gap: '15px' },
    msgUser: { alignSelf: 'flex-end', background: '#1a1a1a', border: '1px solid #333', padding: '15px', borderRadius: '10px 10px 0 10px', maxWidth: '70%' },
    msgAi: { alignSelf: 'flex-start', background: 'rgba(255, 51, 51, 0.1)', border: '1px solid #ff3333', padding: '15px', borderRadius: '10px 10px 10px 0', maxWidth: '70%' },
    msgHeader: { fontSize: '0.7rem', color: '#666', marginBottom: '5px', fontFamily: 'Orbitron' },
    msgBody: { fontSize: '0.95rem', lineHeight: '1.4' },
    typingIndicator: { color: '#666', fontSize: '1.5rem', marginLeft: '10px', marginTop: '10px' },
    dotAnim: { animation: 'blink 1s infinite' },

    inputArea: { padding: '20px', background: '#111', borderTop: '1px solid #333', display: 'flex', gap: '10px' },
    input: { flex: 1, background: '#000', border: '1px solid #333', padding: '10px 15px', color: '#fff', outline: 'none' },
    micBtn: { background: 'none', border: '1px solid #333', color: '#D4AF37', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    sendBtn: { background: '#D4AF37', border: 'none', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },

    // COACHING PANEL (RIGHT)
    coachingPanel: { flex: 1, background: '#111', border: '1px solid #333', borderRadius: '8px', display: 'flex', flexDirection: 'column' },
    panelHeader: { padding: '15px', background: '#000', borderBottom: '1px solid #333', color: '#D4AF37', fontFamily: 'Orbitron', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '10px' },

    analysisBox: { padding: '20px', borderBottom: '1px solid #222' },
    metricRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '0.8rem', fontFamily: 'Orbitron' },

    whisperBox: { padding: '20px', background: 'rgba(212, 175, 55, 0.05)', borderBottom: '1px solid #333', flex: 1 },
    whisperTitle: { color: '#D4AF37', fontSize: '0.9rem', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' },
    whisperText: { fontSize: '0.9rem', lineHeight: '1.5', color: '#eee', fontStyle: 'italic', marginBottom: '20px' },

    actionRow: { display: 'flex', gap: '10px' },
    actionBtn: { flex: 1, background: '#D4AF37', border: 'none', padding: '8px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' },
    actionBtnOutline: { flex: 1, background: 'transparent', border: '1px solid #444', color: '#666', padding: '8px', cursor: 'pointer', fontSize: '0.7rem' },

    alertBox: { margin: '20px', padding: '15px', background: 'rgba(255, 51, 51, 0.1)', border: '1px solid #ff3333', display: 'flex', gap: '10px', alignItems: 'center', color: '#ff3333' }
};

export default SalesLab;